/**
 * @file reportServices.js
 * @description API layer for the Report Configuration module.
 *              Backend speaks snake_case; the UI speaks camelCase.
 *              All translation happens here so components never touch
 *              raw column names.
 */

import apiClient from "../apiInstance.js";
import { REPORT_URLS, COMMON_URLS } from "../apiConfig";
import {
    DEFAULT_TIME_ZONE_KEY, normalizeZoneKey,
} from "../../common/constants/reportModule/reportConstants";

// ── Mappers ────────────────────────────────────────────────────────

/** Recipients arrive either as an array or a comma/semicolon-delimited string. */
const toRecipientArray = (val) => {
    if (Array.isArray(val)) return val.filter(Boolean);
    if (typeof val === "string") {
        return val.split(/[;,]/).map(s => s.trim()).filter(Boolean);
    }
    return [];
};

/** "06:00:00" | "06:00:00.0000000" | "06:00" → "06:00" (for <input type="time">) */
const toTimeInput = (val) => {
    if (!val) return "";
    const m = String(val).match(/(\d{1,2}):(\d{2})/);
    return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
};

/** "06:00" → "06:00:00" (SQL TIME) */
const toSqlTime = (val) => {
    const t = toTimeInput(val);
    return t ? `${t}:00` : null;
};

/** Any date-ish value → "YYYY-MM-DD" for <input type="date">, no UTC shift. */
const toDateInput = (val) => {
    if (!val) return "";
    const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
};

export const mapReportFromApi = (r) => ({
    reportId:    r.report_id,
    reportKey:   r.report_key,
    reportName:  r.report_name,
    description: r.description ?? "",
    isActive:    !!r.is_active,
});

export const mapScheduleFromApi = (s) => ({
    scheduleId:     s.schedule_id,
    reportId:       s.report_id,
    reportKey:      s.report_key ?? "",
    reportName:     s.report_name ?? "",
    // The schedule's own blurb, written by whoever set it up. Distinct from
    // report_description, which is the catalog copy for the report itself.
    scheduleName:   s.schedule_name ?? "",
    description:    s.description ?? "",
    reportDescription: s.report_description ?? "",
    recurrence:     s.recurrence ?? "daily",
    dayOfWeek:      s.day_of_week  == null ? null : Number(s.day_of_week),
    dayOfMonth:     s.day_of_month == null ? null : Number(s.day_of_month),
    runDate:        toDateInput(s.run_date),
    generationTime: toTimeInput(s.generation_time),
    recipients:     toRecipientArray(s.recipients),
    granularity:    s.granularity,
    timeRange:      s.time_range,
    includePdf:     !!s.include_pdf,
    includeCsv:     !!s.include_csv,
    isEnabled:      !!s.is_enabled,
    lastRunDate:    s.last_run_date ?? null,
    lastRunStatus:  s.last_run_status ?? null,
    nextRunDate:    s.next_run_date ?? null,
    timeZoneKey:    s.time_zone_key ?? null,
    createdBy:      s.created_by ?? null,
    createdDate:    s.created_date ?? null,
});

export const mapScheduleToApi = (s) => ({
    schedule_id:     s.scheduleId ?? null,
    report_id:       s.reportId,
    schedule_name:   (s.scheduleName || "").trim(),
    description:     (s.description  || "").trim() || null,
    recurrence:      s.recurrence,
    // Only send the field the chosen recurrence actually uses — everything
    // else goes back to NULL so stale values can't drive the scheduler.
    day_of_week:     s.recurrence === "weekly"  ? Number(s.dayOfWeek)  : null,
    day_of_month:    s.recurrence === "monthly" ? Number(s.dayOfMonth) : null,
    run_date:        s.recurrence === "none"    ? (toDateInput(s.runDate) || null) : null,
    generation_time: toSqlTime(s.generationTime),
    recipients:      toRecipientArray(s.recipients),
    granularity:     s.granularity,
    time_range:      s.timeRange,
    include_pdf:     s.includePdf ? 1 : 0,
    include_csv:     s.includeCsv ? 1 : 0,
    is_enabled:      s.isEnabled ? 1 : 0,
});

// ── Client time zone ───────────────────────────────────────────────

/**
 * The zone every schedule's run time is interpreted in — set once in
 * Configuration → Time Settings (ClientConfiguration.ClientTimezone).
 * Returns a short key: "UTC" | "PT" | "MT" | "CT" | "ET" | "AKT" | "HST".
 *
 * Two sources, in order: the dedicated time zone endpoint, then the full
 * client config the Configuration screen reads. The fallback exists because
 * a silent UTC default is indistinguishable from a real UTC setting — the
 * page just shows the wrong clock, which is exactly how this failed before.
 *
 * NOTE: if either endpoint is POST in your client, swap apiClient.get(url)
 *       for apiClient.post(url, {}).
 */
export const fetchClientTimeZone = async () => {
    // Any field on the payload whose value maps to a real zone. Saves
    // guessing whether the key is ClientTimezone, timeZone, tz, or other.
    const scan = (payload) => {
        const row = Array.isArray(payload) ? payload[0] : payload;
        if (!row) return null;
        if (typeof row === "string") return normalizeZoneKey(row);
        for (const value of Object.values(row)) {
            if (typeof value !== "string") continue;
            const key = normalizeZoneKey(value);
            if (key) return key;
        }
        return null;
    };

    try {
        const { data } = await apiClient.get(COMMON_URLS.GET_CLIENT_TIMEZONE);
        const key = scan(data);
        if (key) return key;
        console.warn("[reports] getclienttimezone returned no recognizable zone:", data);
    } catch (e) {
        console.warn("[reports] getclienttimezone failed, trying client config.", e);
    }

    try {
        const { data } = await apiClient.get(COMMON_URLS.GET_CLIENT_CONFIG);
        const key = scan(data);
        if (key) return key;
    } catch (e) {
        console.warn("[reports] getclientconfig lookup failed.", e);
    }

    console.warn(
        "[reports] Falling back to UTC. Schedule times will display and run in UTC " +
        "until Configuration → Time Settings resolves to a known zone."
    );
    return DEFAULT_TIME_ZONE_KEY;
};

// ── Report catalog (the dropdown) ──────────────────────────────────

/** @returns {Promise<Array>} active reports available for scheduling */
export const fetchReportCatalog = async () => {
    const { data } = await apiClient.get(REPORT_URLS.FETCH_REPORT_CATALOG);
    return (data || []).map(mapReportFromApi);
};

// ── Schedules ──────────────────────────────────────────────────────

/** @param {{ reportId?: number, enabledOnly?: boolean }} [filters] */
export const fetchReportSchedules = async (filters = {}) => {
    const params = {};
    if (filters.reportId)    params.report_id    = filters.reportId;
    if (filters.enabledOnly) params.enabled_only = 1;

    const { data } = await apiClient.get(REPORT_URLS.FETCH_REPORT_SCHEDULES, { params });
    return (data || []).map(mapScheduleFromApi);
};

export const fetchReportSchedule = async (scheduleId) => {
    const { data } = await apiClient.get(`${REPORT_URLS.FETCH_REPORT_SCHEDULE}/${scheduleId}`);
    return data ? mapScheduleFromApi(data) : null;
};

export const createReportSchedule = async (schedule) => {
    const { data } = await apiClient.post(
        REPORT_URLS.CREATE_REPORT_SCHEDULE,
        mapScheduleToApi(schedule)
    );
    return data ? mapScheduleFromApi(data) : null;
};

export const updateReportSchedule = async (scheduleId, schedule) => {
    const { data } = await apiClient.put(
        `${REPORT_URLS.UPDATE_REPORT_SCHEDULE}/${scheduleId}`,
        mapScheduleToApi(schedule)
    );
    return data ? mapScheduleFromApi(data) : null;
};

/** Enable / disable without opening the editor. */
export const toggleReportSchedule = async (scheduleId, isEnabled) => {
    const { data } = await apiClient.patch(
        `${REPORT_URLS.TOGGLE_REPORT_SCHEDULE}/${scheduleId}/enabled`,
        { is_enabled: isEnabled ? 1 : 0 }
    );
    return data ? mapScheduleFromApi(data) : null;
};

export const deleteReportSchedule = async (scheduleId) => {
    const { data } = await apiClient.delete(`${REPORT_URLS.DELETE_REPORT_SCHEDULE}/${scheduleId}`);
    return data;
};

/** Queue an out-of-band run of an existing schedule (same recipients/outputs). */
export const runReportNow = async (scheduleId) => {
    const { data } = await apiClient.post(`${REPORT_URLS.RUN_REPORT_NOW}/${scheduleId}/run`, {});
    return data;
};

/** Delivery history for one schedule (drill-down). */
export const fetchReportRunHistory = async (scheduleId) => {
    const { data } = await apiClient.get(`${REPORT_URLS.FETCH_REPORT_RUNS}/${scheduleId}/runs`);
    return data || [];
};

/**
 * Pulls a generated PDF/CSV down as a blob and hands the browser a save
 * dialog. Goes through apiClient rather than a bare link so the JWT rides
 * along — the download route sits behind checkJwt like everything else.
 */
export const downloadReportFile = async (runId, format = "pdf", filename) => {
    const { data, headers } = await apiClient.get(
        `${REPORT_URLS.DOWNLOAD_REPORT_FILE}/${runId}`,
        { params: { format }, responseType: "blob" }
    );

    // Prefer the server's filename; it already carries the report name and range.
    const disposition = headers?.["content-disposition"] || "";
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const name  = filename || match?.[1] || `report_${runId}.${format}`;

    const url = URL.createObjectURL(new Blob([data]));
    const a   = document.createElement("a");
    a.href     = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};